import * as React from 'react'
import { connect } from 'react-redux'
import { RouteComponentProps } from 'react-router-dom'
import { noop } from 'lodash'

import NavBar from '@doordash/component-navbar'
import Separator from '@doordash/component-separator'
import {
  Text,
  Colors,
  Icon,
  InlineChildren,
  Inset,
  Stack,
  StackChildren,
} from '@doordash/design-language'

import * as actions from '../../../../actions'
import { DeliveryIssue } from '../../../../models/types'
import { RootState } from '../../../../reducers'
import routes from '../../../../routes'
import {
  deliveryIssueDetailsSelector,
  getDasherId,
  hasSubmittedDisputeSelector,
} from '../../../../selectors/deliveryIssues'
import ListCellMenuItem from '../components/ListCellMenuItem'
import { countMissedSteps, formatTimestamp } from '../utilities'
import { OppositeHeadings } from './styles'
import DeliveryIssueStep, { StepPositions } from './DeliveryIssueStep'
import LearnMore from '../components/LearnMore'
import { StickyTop } from '../components/styles'
import ListCellMessage from './ListCellMessage'
import t from './strings'

type DeliveryIssueDetailsProps = {
  dasherId: string
  deliveryIssueDetails: DeliveryIssue
  getDeliveryIssueDetails: (dasherId: string, issueId: string) => void
  issueId: string
  hasSubmittedDispute: boolean
}

/*
  We show the details of a delivery issue here by making a
  network request for the by the delivery issue ID retrieved via props.
 */
export const DeliveryIssueDetails: React.FC<DeliveryIssueDetailsProps &
  RouteComponentProps> = ({
  dasherId,
  deliveryIssueDetails,
  getDeliveryIssueDetails,
  hasSubmittedDispute,
  history,
  issueId,
}) => {
  /*
   * We can't just pass details data from the list as props because we might
   * link directly to this detail page and it'd need to fetch its own data.
   */
  React.useEffect(() => {
    // Fetch details of an issue by dasher ID and issue ID.
    // Also will mark the issue status as "read" if it's "unread".
    getDeliveryIssueDetails(dasherId, issueId)
  }, [dasherId, issueId])

  const { storeName, createdAt, steps } = deliveryIssueDetails

  return (
    <div>
      <StickyTop>
        <NavBar
          title={storeName}
          onNavigationClick={() => {
            // Go back to delivery issues list
            history.push(routes.deliveryIssueList.path)
          }}
          onOverflowClick={noop}
          textAlign={NavBar.TextAlignments.Center}
        />
      </StickyTop>
      <Inset>
        <Stack>
          <Text styles={Text.Styles.PageTitle}>{t.merchantTitleText()}</Text>
        </Stack>
        <StackChildren size={Stack.Sizes.XSmall}>
          <InlineChildren>
            <Icon type={Icon.Types.OrderHotbagLine} />
            <Text>{storeName}</Text>
          </InlineChildren>
          <InlineChildren>
            <Icon type={Icon.Types.CalendarLine} />
            <Text>{formatTimestamp(createdAt)}</Text>
          </InlineChildren>
        </StackChildren>
      </Inset>
      <Separator />
      <Inset>
        <OppositeHeadings>
          <Text styles={Text.Styles.ListRowTitleLarge}>
            {t.requiredStepsTitleText()}
          </Text>
          <Text styles={Text.Styles.Bold14}>
            {t.missingSteps({
              missed: `${countMissedSteps(steps).missed}`,
              total: `${countMissedSteps(steps).total}`,
            })}
          </Text>
        </OppositeHeadings>
        <Text
          styles={Text.Styles.PageDescriptionBody}
          color={Colors.SystemGrey60}
        >
          {t.requiredStepsDescriptionText()}
        </Text>
      </Inset>
      {steps &&
        steps.map(({ type, result }, index) => {
          let position = StepPositions.Other

          if (index === 0) {
            position = StepPositions.First
          }
          if (index === steps.length - 1) {
            position = StepPositions.Last
          }

          return (
            <DeliveryIssueStep
              key={type}
              type={type}
              result={result}
              position={position}
            />
          )
        })}
      <Separator />
      <Inset>
        <StackChildren>
          <Text styles={Text.Styles.ListRowTitleLarge}>
            {t.additionalInfoTitleText()}
          </Text>
          <Text
            styles={Text.Styles.PageDescriptionBody}
            color={Colors.SystemGrey60}
          >
            {t.additionalInfoDescriptionText()}
          </Text>
          <LearnMore />
        </StackChildren>
      </Inset>
      <Separator type={Separator.Types.FullBorder} />
      {hasSubmittedDispute ? (
        <ListCellMessage text={t.additionalDetailsSubmittedText()} />
      ) : (
        <ListCellMenuItem
          title={t.additionalDetailsNotSubmittedText()}
          onClick={() => {
            history.push(routes.deliveryDispute.buildPath({ id: issueId }))
          }}
        />
      )}
    </div>
  )
}

const mapStateToProps = (state: RootState) => ({
  dasherId: getDasherId(state),
  deliveryIssueDetails: deliveryIssueDetailsSelector(state),
  hasSubmittedDispute: hasSubmittedDisputeSelector(state),
})

const mapDispatchToProps = {
  getDeliveryIssueDetails: actions.getDeliveryIssueDetails,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(DeliveryIssueDetails)
